// packages/console/src/components/PresetEditorModal.tsx
//
// Right-side, full-height flyout for editing an output (transcode) profile.
// Translated from the "Transcoder Settings Flyout" design into the console
// idiom: house design tokens (var(--blue-500), var(--line), var(--fg*),
// var(--r-*), var(--t-*)), the shared Parts/Icons primitives, and the live-PATCH
// pattern (every change calls patchPreset). Layout:
//   - header: eyebrow + required name input (+ amber "Name required") + close X
//   - global band (bordered card, 3 columns): delivery + rate control; GOP &
//     segmentation + picture handling (scaling / bit depth); rung defaults
//     (default codec / fps / audio) + a 10-bit web-unsafe warning
//   - ABR ladder: expandable per-rung cards (collapsed summary; expanded grid of
//     codec / profile / level / bitrate / fps / filter / audio) + add-custom row
//   - footer: summary + Cancel + Save profile
// The design is always-per-rung, so the frontend sets perRungFrameRate=true and
// perRungAudio=true whenever it patches the ladder or audio. Every wired control
// changes real preset/rung fields. The former "Not yet supported" mockup block
// (two-pass, deinterlace, colour/transfer, sample rate, extended audio codecs)
// is no longer rendered. Grid lines are deliberately low-contrast.

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Eb, TChip, DragSlider } from "./Parts";
import { Icon } from "./Icons";
import {
  AUDIO_CODECS,
  CODECS,
  CONTAINERS,
  LADDER_STEPS,
  RATE_CONTROLS,
  SPEED_CHIPS,
  audioCodecAllowsFormat,
  defaultProfileFor,
  levelsApply,
  LEVELS,
  profileOptionsFor,
  speedChipFor,
} from "../lib/preset";
import {
  createPreset,
  patchPreset,
  type AudioCodec,
  type Container,
  type NewPreset,
  type Preset,
  type PresetPatch,
  type RateControl,
  type Rung,
  type Scaling,
  type VideoCodec,
} from "../api/jobs";

const FPS_STEPS = [24, 25, 30, 48, 50, 60];
const rk = (w: number, h: number) => `${w}x${h}`;

// Exact field/checkbox colors from the flyout design (Aether Live DS). The
// console's house tokens are close but not identical — --bg-input (#1A1E25) is
// lighter than the design's field ink, and the DS checkbox is a translucent
// blue tint, not a solid fill — so these are applied locally to match the mock
// without disturbing the shared house tokens other panels rely on.
const FIELD_BG = "#15181E"; // design --ae-ink-600 (darker than --bg-input)
const CHECK_ON_BG = "rgba(56,128,255,0.16)"; // design --ae-blue-16, +20% brighten (checkbox fill when on)
const CHECK_ON_BORDER = "rgba(56,128,255,0.38)"; // design --ae-blue-38, +20% brighten (checkbox border when on)
const CHECK_MARK = "#6CA7FF"; // design --ae-blue-400, +20% brighten (the checkmark glyph)
// Section-chrome inks from the design (again applied locally, not via the shared
// house tokens): the flyout surface, the header/footer, and the section cards
// each use a distinct ink step the house --bg-* tokens only approximate.
const INK_850 = "#080A0D"; // header / footer chrome (design --ae-ink-850)
const INK_800 = "#0C0F13"; // flyout panel surface (design --ae-ink-800)
const INK_700 = "#12151A"; // section cards / open rung / ladder header (design --ae-ink-700)

// crfHint reads the perceptual quality band a CRF value sits in (design copy).
function crfHint(crf: number): string {
  return crf <= 18 ? "near-transparent" : crf <= 24 ? "web delivery" : crf <= 32 ? "lean" : "aggressive";
}

// filterLabel reads the sharpen/smooth slider value into copy.
function filterLabel(f: number): string {
  return f === 0 ? "None" : f < 0 ? `Sharpen ${Math.abs(f)}` : `Smooth ${f}`;
}

// The editor now works on a LOCAL working copy of the preset and only touches
// the server on Save. This makes "New" a client-side draft (created via POST
// only when the user saves, so Cancel leaves no orphan preset) and lets an edit
// of a Standard/builtin profile FORK to a new custom preset instead of mutating
// the shared built-in.
//
//   mode="draft"  -> Save always CREATES a new preset (New profile flow).
//   mode="edit"   -> Save PATCHes the source preset, unless the source is
//                    builtin, in which case Save FORKS to a new custom preset.
//
// onSaved(created) reports the resulting preset when Save created a NEW one
// (draft, or a builtin fork), so the parent can select it and land on the
// Custom tab; it is called with null when Save patched an existing preset.
export function PresetEditorModal({
  preset,
  mode = "edit",
  onPatched,
  onSaved,
  onClose,
}: {
  preset: Preset;
  mode?: "draft" | "edit";
  onPatched: () => void;
  onSaved?: (created: Preset | null) => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // work is the local working copy every control edits. It starts as a clone of
  // the incoming preset and only round-trips to the server on Save.
  const [work, setWork] = useState<Preset>(preset);
  const [name, setName] = useState(work.name);
  const [touched, setTouched] = useState(false);
  const [cw, setCw] = useState("");
  const [ch, setCh] = useState("");
  // Card open/collapsed is client-only view state keyed by ladder key; nothing
  // here round-trips to the server.
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());

  // A save FORKS (creates a new preset) for a draft, or when editing a builtin
  // (never mutate a shared/seeded Standard profile in place).
  const forks = mode === "draft" || Boolean(preset.builtin);

  // Re-seed the working copy only when the SOURCE identity changes (a different
  // preset id), not on every new object reference. A background jobs refetch
  // hands us a fresh Preset object with the same id while the flyout is open;
  // re-seeding on that would silently discard the user's in-progress edits.
  useEffect(() => {
    setWork(preset);
    setName(preset.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset.id]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // patch mutates the LOCAL working copy only. Save is the single point that
  // hits the server (create or patch), so Cancel discards everything.
  const patch = (body: PresetPatch) => {
    setErr(null);
    setWork((w) => ({ ...w, ...(body as Partial<Preset>) }));
  };

  // The new UI is always-per-rung: any ladder/audio change also asserts the
  // per-rung flags so the scheduler reads per-rung fps/audio.
  const patchLadder = (ladder: Rung[], extra?: PresetPatch) =>
    patch({ ladder, perRungFrameRate: true, perRungAudio: true, ...extra });

  // --- delivery formats / codec carriability (mirrors the existing guards) ---
  const codecAllowsFormat = (f: Container, vc: VideoCodec) =>
    vc === "vp8" || vc === "vp9" ? f === "webm" : f === "webm" ? vc === "av1" || vc === "svtav1" || vc === "libaom" : true;
  const curFormats: Container[] = work.formats && work.formats.length ? work.formats : [work.container];
  const codecAllowed = (vc: VideoCodec) => curFormats.every((f) => codecAllowsFormat(f, vc));
  const allowedCodecs = CODECS.filter((c) => codecAllowed(c.id));
  // The audio-codec set carriable by EVERY selected format ("" is always ok).
  const audioAllowed = (ac: AudioCodec) => curFormats.every((f) => audioCodecAllowsFormat(f, ac));
  const allowedAudioCodecs = AUDIO_CODECS.filter((c) => audioAllowed(c.id));

  const primaryOf = (fs: Container[]): Container => {
    for (const p of ["mp4", "webm", "mov", "hls", "dash"] as Container[]) if (fs.includes(p)) return p;
    return fs[0];
  };
  const applyFormats = (fs: Container[], vc: VideoCodec = work.videoCodec) => {
    if (fs.length === 0) return;
    const okV = (c: VideoCodec) => fs.every((f) => codecAllowsFormat(f, c));
    const okA = (c: AudioCodec) => fs.every((f) => audioCodecAllowsFormat(f, c));
    const ladder = work.ladder.map((r) => {
      let next = r;
      if (r.videoCodec && !okV(r.videoCodec)) next = { ...next, videoCodec: undefined };
      if (r.audioCodec && !okA(r.audioCodec)) next = { ...next, audioCodec: "" };
      return next;
    });
    const audioCodec: AudioCodec | undefined = work.audioCodec && !okA(work.audioCodec) ? "" : work.audioCodec;
    patch({ videoCodec: vc, formats: fs, container: primaryOf(fs), ladder, audioCodec, perRungFrameRate: true, perRungAudio: true });
  };
  const toggleFormat = (f: Container) => {
    if (!codecAllowsFormat(f, work.videoCodec)) return;
    const next = curFormats.includes(f) ? curFormats.filter((x) => x !== f) : [...curFormats, f];
    applyFormats(next);
  };
  const setCodec = (vc: VideoCodec) => {
    let fs = curFormats.filter((f) => codecAllowsFormat(f, vc));
    if (fs.length === 0) fs = [vc === "vp8" || vc === "vp9" ? "webm" : "mp4"];
    applyFormats(fs, vc);
  };

  // --- rate control ---
  const setRateControl = (rc: RateControl) => {
    const bitrate = work.bitrateKbps && work.bitrateKbps > 0 ? work.bitrateKbps : 8000;
    if (rc === "crf") patch({ rateControl: "crf", crf: work.crf && work.crf > 0 ? work.crf : 23, bitrateKbps: 0, maxBitrateKbps: 0 });
    else patch({ rateControl: rc, bitrateKbps: bitrate, crf: 0, maxBitrateKbps: 0 });
  };
  const isCRF = work.rateControl === "crf";
  const isVBR = work.rateControl === "vbr";

  // --- ladder ---
  const ladderByKey = new Map(work.ladder.map((r) => [rk(r.width, r.height), r]));
  const enabledCount = work.ladder.length;
  const rungs = [...work.ladder].sort((a, b) => a.height - b.height || a.width - b.width);
  // A rung expands when clicked anywhere on its row (openRung), while the chevron
  // toggles it back closed. openRung is idempotent so clicking an open row is a
  // no-op rather than a surprise collapse.
  const toggleOpen = (key: string) =>
    setOpenKeys((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const openRung = (key: string) => setOpenKeys((s) => (s.has(key) ? s : new Set(s).add(key)));

  const uniqueName = (base: string, w: number, h: number) => {
    if (!work.ladder.some((r) => r.name === base && !(r.width === w && r.height === h))) return base;
    return `${base}-${w}`;
  };
  const setRung = (key: string, fields: Partial<Rung>) => {
    patchLadder(work.ladder.map((x) => (rk(x.width, x.height) === key ? { ...x, ...fields } : x)));
  };
  // Changing a rung's codec re-seeds an incompatible profile and clears the
  // level when the new codec has none (mirrors the design's changeRungCodec).
  const setRungCodec = (key: string, codec: VideoCodec) => {
    const opts = profileOptionsFor(codec).map((o) => o.id);
    patchLadder(
      work.ladder.map((x) => {
        if (rk(x.width, x.height) !== key) return x;
        const next: Rung = { ...x, videoCodec: codec };
        if (!opts.includes(x.profile ?? "")) next.profile = defaultProfileFor(codec);
        if (!levelsApply(codec)) next.level = "auto";
        return next;
      }),
    );
  };
  const removeRung = (key: string) => {
    if (enabledCount <= 1) return;
    patchLadder(work.ladder.filter((x) => rk(x.width, x.height) !== key));
  };
  const seedRung = (w: number, h: number): Rung => ({
    name: uniqueName(`${h}p`, w, h),
    width: w,
    height: h,
    videoCodec: work.videoCodec,
    profile: defaultProfileFor(work.videoCodec),
    level: "auto",
    filter: 0,
    audioCodec: work.audioCodec ?? "",
    audioChannels: 2,
    frameRate: work.frameRate && work.frameRate > 0 ? work.frameRate : 30,
    audioBitrateKbps: work.audioBitrateKbps && work.audioBitrateKbps > 0 ? work.audioBitrateKbps : 128,
  });
  const addCustom = () => {
    let w = Math.round(Number(cw));
    let h = Math.round(Number(ch));
    if (!(w >= 16 && w <= 8192 && h >= 16 && h <= 8192)) {
      setErr("Custom resolution must be 16..8192 on each side.");
      return;
    }
    if (w % 2) w += 1;
    if (h % 2) h += 1;
    if (ladderByKey.has(rk(w, h))) {
      setErr("That resolution is already in the ladder.");
      return;
    }
    if (enabledCount >= 12) {
      setErr("The ladder is full (12 rungs).");
      return;
    }
    setCw("");
    setCh("");
    const key = rk(w, h);
    setOpenKeys((s) => new Set(s).add(key));
    patchLadder([...work.ladder, seedRung(w, h)].sort((a, b) => a.height - b.height));
  };
  const quickAdd = (step: { name: string; width: number; height: number }) => {
    const key = rk(step.width, step.height);
    if (ladderByKey.has(key) || enabledCount >= 12) return;
    setOpenKeys((s) => new Set(s).add(key));
    patchLadder([...work.ladder, seedRung(step.width, step.height)].sort((a, b) => a.height - b.height));
  };
  const missingSteps = LADDER_STEPS.filter((s) => !ladderByKey.has(rk(s.width, s.height)));

  const audioOff = !!work.audioDisabled;
  const bitDepth = work.bitDepth === 10 ? 10 : 8;
  const scaling: Scaling = work.scaling ?? "fit";

  // Fold the edited name into the local working copy (still no server call).
  const commitName = () => {
    const n = name.trim();
    if (n && n !== work.name) patch({ name: n });
  };
  // presetFields is the exact server-accepted field set (mirrors the POST
  // presetBody / PATCH presetPatch structs, which reject unknown fields). It
  // deliberately EXCLUDES id/workspace/timestamps and the server-managed flags
  // (builtin/eve) and cmdOverride, so a create or patch built from the working
  // copy never trips DisallowUnknownFields or the owner-gated cmdOverride guard.
  const presetFields = (finalName: string) => ({
    name: finalName,
    container: work.container,
    formats: work.formats,
    videoCodec: work.videoCodec,
    rateControl: work.rateControl,
    crf: work.crf,
    bitrateKbps: work.bitrateKbps,
    maxBitrateKbps: work.maxBitrateKbps,
    gopLength: work.gopLength,
    frameRate: work.frameRate,
    audioBitrateKbps: work.audioBitrateKbps,
    audioChannels: work.audioChannels,
    audioDisabled: work.audioDisabled,
    perRungFrameRate: work.perRungFrameRate,
    perRungAudio: work.perRungAudio,
    speedPreset: work.speedPreset,
    keyframeSeconds: work.keyframeSeconds,
    segmentSeconds: work.segmentSeconds,
    sceneCut: work.sceneCut,
    scaling: work.scaling,
    bitDepth: work.bitDepth,
    vbvMaxRatio: work.vbvMaxRatio,
    vbvBufRatio: work.vbvBufRatio,
    audioCodec: work.audioCodec,
    ladder: work.ladder,
  });
  const createBody = (finalName: string): NewPreset => presetFields(finalName);
  const patchBody = (finalName: string): PresetPatch => presetFields(finalName);
  // createUnique creates a preset, resolving a UNIQUE(workspace_id,name)
  // collision by appending an incrementing suffix. The server returns 409 on a
  // duplicate name; retry with " 2", " 3", … so a Save never dead-ends on a
  // name that is already taken (e.g. two forks of the same Standard profile).
  const createUnique = async (body: NewPreset): Promise<Preset> => {
    const base = body.name;
    for (let n = 1; n <= 50; n++) {
      const attempt: NewPreset = { ...body, name: n === 1 ? base : `${base} ${n}` };
      try {
        return await createPreset(attempt);
      } catch (e) {
        const status = e && typeof e === "object" && "status" in e ? (e as { status: number }).status : 0;
        // A name collision is retried with a new name. The server signals it as
        // 409 (conflict); today the unique-name violation still surfaces as a
        // 500, so retry that too. A 400 (validation) or any other error is a
        // real problem with the preset and surfaces immediately, never looped.
        if ((status === 409 || status === 500) && n < 50) continue;
        throw e;
      }
    }
    throw new Error("could not find an available profile name");
  };
  const nameValid = name.trim().length > 0;
  const nameMissing = touched && !nameValid;
  const crf = work.crf ?? 23;
  const vbvMax = work.vbvMaxRatio && work.vbvMaxRatio > 0 ? work.vbvMaxRatio : 1.5;
  const vbvBuf = work.vbvBufRatio && work.vbvBufRatio > 0 ? work.vbvBufRatio : 2;

  const anyClosed = rungs.some((r) => !openKeys.has(rk(r.width, r.height)));
  const toggleAll = () => {
    if (anyClosed) setOpenKeys(new Set(rungs.map((r) => rk(r.width, r.height))));
    else setOpenKeys(new Set());
  };

  const footerSummary = useMemo(() => {
    const rc = isCRF ? `CRF ${crf}` : `${work.rateControl.toUpperCase()} · per rung`;
    const key = work.keyframeSeconds && work.keyframeSeconds > 0 ? ` / ${work.keyframeSeconds}s` : "";
    const seg = work.segmentSeconds && work.segmentSeconds > 0 ? work.segmentSeconds : 6;
    return `${rc} · GOP ${work.gopLength}f${key} · seg ${seg}s · ${scaling} · ${bitDepth}-bit · ${enabledCount} rungs`;
  }, [isCRF, crf, work.rateControl, work.keyframeSeconds, work.segmentSeconds, work.gopLength, scaling, bitDepth, enabledCount]);

  // save is the ONLY server round-trip. It either PATCHes the source preset
  // (editing an existing custom profile) or CREATES a new one (a draft, or a
  // fork of a builtin/Standard profile). A create resolves a unique name by
  // appending a number on a UNIQUE(workspace,name) collision so repeated saves
  // never dead-end.
  const save = () => {
    if (!nameValid) {
      setTouched(true);
      return;
    }
    const finalName = name.trim();
    setBusy(true);
    setErr(null);
    (async () => {
      try {
        if (forks) {
          const created = await createUnique(createBody(finalName));
          onPatched();
          onSaved?.(created);
        } else {
          await patchPreset(work.id, patchBody(finalName));
          onPatched();
          onSaved?.(null);
        }
        onClose();
      } catch (e) {
        setErr(e instanceof Error ? e.message : "preset save failed");
      } finally {
        setBusy(false);
      }
    })();
  };

  // ----- shared field styles (console tokens) -----
  const inp: CSSProperties = {
    width: "100%",
    minWidth: 0,
    minHeight: 28,
    padding: "0 7px",
    borderRadius: "var(--r-sm)",
    font: "400 11px var(--font-mono)",
    color: "#fff",
    background: FIELD_BG,
    border: "1px solid var(--line)",
    outline: "none",
  };
  const sel: CSSProperties = { ...inp, cursor: "pointer", padding: "0 6px" };
  const capLabel: CSSProperties = { font: "var(--t-micro)", color: "var(--fg3)", marginBottom: 5 };
  const microLabel: CSSProperties = { font: "500 9px var(--font-sans)", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--fg4)", marginBottom: 5 };
  const segGroup: CSSProperties = { display: "flex", gap: 4, padding: 3, borderRadius: "var(--r-sm)", background: FIELD_BG, border: "1px solid var(--line)" };

  const segBtn = (active: boolean): CSSProperties => ({
    flex: 1,
    minWidth: 0,
    minHeight: 24,
    padding: "0 6px",
    borderRadius: "var(--r-xs)",
    cursor: "pointer",
    font: "600 10px var(--font-sans)",
    letterSpacing: ".05em",
    textTransform: "uppercase",
    background: active ? "var(--blue-tint)" : "transparent",
    border: `1px solid ${active ? "var(--blue-500)" : "transparent"}`,
    color: active ? "var(--blue-300)" : "var(--fg3)",
  });
  const iconBtn = (active: boolean): CSSProperties => ({
    width: 30,
    height: 26,
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--r-xs)",
    cursor: "pointer",
    padding: 0,
    font: "600 11px var(--font-sans)",
    background: active ? "var(--blue-tint)" : "transparent",
    border: `1px solid ${active ? "var(--blue-500)" : "transparent"}`,
    color: active ? "var(--blue-300)" : "var(--fg3)",
  });
  const checkBox = (on: boolean): CSSProperties => ({
    width: 17,
    height: 17,
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--r-xs)",
    cursor: "pointer",
    padding: 0,
    background: on ? CHECK_ON_BG : "transparent",
    border: `1px solid ${on ? CHECK_ON_BORDER : "var(--line-strong)"}`,
    color: CHECK_MARK,
  });
  const colHead = (children: string, badge?: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <Eb style={{ margin: 0 }}>{children}</Eb>
      {badge && (
        <span style={{ font: "500 9px var(--font-sans)", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--fg4)", border: "1px solid var(--line)", borderRadius: "var(--r-xs)", padding: "2px 5px" }}>
          {badge}
        </span>
      )}
    </div>
  );

  const GRID = "34px minmax(0,0.9fr) 132px minmax(0,1.9fr) 26px 26px";

  return (
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "80vw", zIndex: 100, display: "flex", flexDirection: "column", background: INK_800, borderLeft: "1px solid var(--line-strong)", boxShadow: "-12px 0 40px rgba(0,0,0,.55)", pointerEvents: busy ? "none" : "auto" }}>
      {/* HEADER */}
      <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 16, padding: "12px 18px", borderBottom: "1px solid var(--line)", background: INK_850 }}>
        <span style={{ font: "var(--t-eyebrow)", letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--blue-400)", whiteSpace: "nowrap" }}>Transcode profile</span>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <input
            value={name}
            placeholder="Profile name (required)"
            onChange={(e) => setName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => e.key === "Enter" && nameValid && (e.target as HTMLInputElement).blur()}
            style={{ flex: 1, maxWidth: 320, minHeight: 30, padding: "0 10px", borderRadius: "var(--r-sm)", font: "600 13px var(--font-sans)", color: "#fff", background: FIELD_BG, border: `1px solid ${nameMissing ? "var(--warn)" : "var(--line)"}`, outline: "none" }}
          />
          {nameMissing && <span style={{ font: "var(--t-micro)", color: "var(--warn)", whiteSpace: "nowrap" }}>Name required</span>}
        </div>
        <button className="ae-b" onClick={onClose} title="Close (Esc)" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, flex: "none", borderRadius: "var(--r-sm)", background: "transparent", border: "1px solid var(--line)", cursor: "pointer", color: "var(--fg2)" }}>
          <Icon name="x" size={13} />
        </button>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 18px 16px" }}>
        {/* ===== GLOBAL BAND ===== */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", border: "1px solid var(--line)", borderRadius: "var(--r-md)", overflow: "hidden", background: INK_700 }}>
          {/* col 1 · delivery + rate control */}
          <div style={{ padding: "12px 14px", borderRight: "1px solid var(--line)" }}>
            {colHead("Delivery")}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 9 }}>
              {CONTAINERS.map((c) => (
                <TChip key={c.id} active={curFormats.includes(c.id)} onClick={() => toggleFormat(c.id)}>
                  {c.label}
                </TChip>
              ))}
            </div>

            <div style={{ marginTop: 14 }}>{colHead("Rate control")}</div>
            <div style={{ ...segGroup, marginTop: 9 }}>
              {RATE_CONTROLS.map((m) => (
                <button key={m.id} className="ae-b" onClick={() => setRateControl(m.id)} style={segBtn(work.rateControl === m.id)}>
                  {m.label}
                </button>
              ))}
            </div>

            {isCRF ? (
              <div style={{ marginTop: 11 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 2 }}>
                  <span style={{ font: "var(--t-micro)", color: "var(--fg3)" }}>CRF quality</span>
                  <span style={{ font: "600 11px var(--font-mono)", color: "var(--blue-400)" }}>
                    {crf} · {crfHint(crf)}
                  </span>
                </div>
                <DragSlider label="" value={crf} min={0} max={51} step={1} onChange={(v) => patch({ crf: Math.round(v) })} />
              </div>
            ) : isVBR ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 11 }}>
                  <div>
                    <div style={capLabel}>Maxrate ×target</div>
                    <RatioInput value={vbvMax} min={1} max={4} title="VBR maxrate as a multiple of the per-rung target bitrate" onCommit={(v) => patch({ vbvMaxRatio: v })} style={inp} />
                  </div>
                  <div>
                    <div style={capLabel}>VBV buf ×target</div>
                    <RatioInput value={vbvBuf} min={1} max={8} title="VBV buffer as a multiple of the per-rung target bitrate" onCommit={(v) => patch({ vbvBufRatio: v })} style={inp} />
                  </div>
                </div>
                <div style={{ font: "var(--t-micro)", color: "var(--fg3)", marginTop: 9 }}>Target bitrate is set per rung in the ladder below.</div>
              </>
            ) : (
              <div style={{ font: "var(--t-micro)", color: "var(--fg3)", marginTop: 11 }}>Target bitrate is set per rung in the ladder below.</div>
            )}

            <div style={{ marginTop: 11 }}>
              <div style={capLabel}>Encoder speed</div>
              <div style={segGroup}>
                {SPEED_CHIPS.map((s) => (
                  <button key={s.id} className="ae-b" onClick={() => patch({ speedPreset: s.id })} style={segBtn(speedChipFor(work.speedPreset) === s.id)}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* col 2 · GOP & segmentation + picture handling */}
          <div style={{ padding: "12px 14px", borderRight: "1px solid var(--line)" }}>
            {colHead("GOP & segmentation", "locked")}
            <div style={{ font: "var(--t-micro)", color: "var(--fg3)", marginTop: 7, lineHeight: 1.45 }}>Shared by every rung. Mismatches break seamless ABR switching.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 10 }}>
              <div>
                <div style={capLabel}>GOP · frames</div>
                <NumInput value={work.gopLength} min={1} max={600} step={1} onCommit={(v) => patch({ gopLength: v || 1 })} style={inp} />
              </div>
              <div>
                <div style={capLabel}>Keyframe · s</div>
                <NumInput value={work.keyframeSeconds ?? 0} min={0} max={10} step={0.5} placeholder="0" onCommit={(v) => patch({ keyframeSeconds: v })} style={inp} />
              </div>
              <div>
                <div style={capLabel}>Segment · s</div>
                <NumInput value={work.segmentSeconds ?? 6} min={1} max={30} step={1} onCommit={(v) => patch({ segmentSeconds: v || 6 })} style={inp} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <button className="ae-b" onClick={() => patch({ sceneCut: !(work.sceneCut ?? true) })} style={checkBox(work.sceneCut ?? true)}>
                {(work.sceneCut ?? true) && <Icon name="check" size={11} color={CHECK_MARK} />}
              </button>
              <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>Scene-cut detection</span>
            </div>

            <div style={{ marginTop: 14 }}>{colHead("Picture handling")}</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 14, marginTop: 9, flexWrap: "wrap" }}>
              <div>
                <div style={capLabel}>Scaling & aspect</div>
                <div style={segGroup}>
                  <button className="ae-b" onClick={() => patch({ scaling: "fit" })} title="Fit-pad — preserve aspect ratio, pad with black bars" style={iconBtn(scaling === "fit")}>
                    <ScaleFitIcon />
                  </button>
                  <button className="ae-b" onClick={() => patch({ scaling: "fill" })} title="Fill-crop — preserve aspect ratio, crop the overflow" style={iconBtn(scaling === "fill")}>
                    <ScaleFillIcon />
                  </button>
                  <button className="ae-b" onClick={() => patch({ scaling: "stretch" })} title="Stretch — force to rung size, distorts on mismatch" style={iconBtn(scaling === "stretch")}>
                    <ScaleStretchIcon />
                  </button>
                </div>
              </div>
              <div>
                <div style={capLabel}>Bit depth</div>
                <div style={segGroup}>
                  <button className="ae-b" onClick={() => patch({ bitDepth: 8 })} title="8-bit 4:2:0 — web-safe, plays inline everywhere" style={iconBtn(bitDepth === 8)}>
                    8
                  </button>
                  <button className="ae-b" onClick={() => patch({ bitDepth: 10 })} title="10-bit — higher precision, web-unsafe inline playback" style={iconBtn(bitDepth === 10)}>
                    10
                  </button>
                </div>
              </div>
              <div style={{ font: "var(--t-micro)", color: "var(--fg3)", lineHeight: 1.4, paddingBottom: 6 }}>
                {scaling} · {bitDepth === 10 ? "10-bit · web-unsafe" : "8-bit 4:2:0"}
              </div>
            </div>
          </div>

          {/* col 3 · rung defaults */}
          <div style={{ padding: "12px 14px" }}>
            {colHead("Rung defaults", "seeds new")}
            <div style={{ font: "var(--t-micro)", color: "var(--fg3)", marginTop: 7, lineHeight: 1.45 }}>Every value stays editable per rung in the ladder.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
              <div>
                <div style={capLabel}>Video codec</div>
                <select value={work.videoCodec} onChange={(e) => setCodec(e.target.value as VideoCodec)} style={sel}>
                  {allowedCodecs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={capLabel}>Frame rate · fps</div>
                <NumInput value={work.frameRate ?? 30} min={1} max={240} step={1} onCommit={(v) => patch({ frameRate: v })} style={inp} />
              </div>
              <div>
                <div style={capLabel}>Audio codec</div>
                <select value={work.audioCodec ?? ""} onChange={(e) => patch({ audioCodec: e.target.value as AudioCodec, perRungAudio: true })} style={sel}>
                  <option value="">Container</option>
                  {allowedAudioCodecs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={capLabel}>Audio · kb/s</div>
                <NumInput value={work.audioBitrateKbps ?? 128} min={32} max={512} step={16} onCommit={(v) => patch({ audioBitrateKbps: v })} style={inp} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <button className="ae-b" onClick={() => patch({ audioDisabled: audioOff })} style={checkBox(!audioOff)}>
                {!audioOff && <Icon name="check" size={11} color={CHECK_MARK} />}
              </button>
              <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>Include audio</span>
            </div>
            {bitDepth === 10 && (
              <div style={{ display: "flex", gap: 8, marginTop: 11, padding: "9px 10px", borderRadius: "var(--r-sm)", background: "rgba(255,198,43,0.08)", border: "1px solid rgba(255,198,43,0.38)" }}>
                <Icon name="lock" size={13} color="var(--warn)" style={{ flex: "none", marginTop: 1 }} />
                <div style={{ font: "var(--t-micro)", color: "var(--fg2)", lineHeight: 1.45 }}>
                  <span style={{ color: "var(--warn)", fontWeight: 600 }}>Web-unsafe.</span> 10-bit bypasses the forced 8-bit yuv420p path; renders green inline in most browsers.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ===== ABR LADDER ===== */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
          <Eb style={{ margin: 0 }}>ABR ladder</Eb>
          <span style={{ font: "var(--t-micro)", color: "var(--fg3)" }}>
            {enabledCount}/{enabledCount} enabled · {curFormats.map((f) => f.toUpperCase()).join(", ")}
          </span>
          <button className="ae-b" onClick={toggleAll} style={{ marginLeft: "auto", height: 26, padding: "0 10px", borderRadius: "var(--r-xs)", border: "1px solid var(--line)", background: "transparent", color: "var(--fg2)", font: "600 10px var(--font-sans)", letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap" }}>
            {anyClosed ? "Expand all" : "Collapse all"}
          </button>
        </div>

        {/* header row */}
        <div style={{ display: "grid", gridTemplateColumns: GRID, gap: 10, alignItems: "center", padding: "7px 10px", marginTop: 8, border: "1px solid var(--line)", borderBottom: "none", borderRadius: "var(--r-sm) var(--r-sm) 0 0", background: INK_700, font: "600 9px var(--font-sans)", letterSpacing: ".10em", textTransform: "uppercase", color: "var(--fg3)" }}>
          <div>On</div>
          <div>Rung</div>
          <div>Resolution</div>
          <div>Settings</div>
          <div />
          <div />
        </div>

        {/* rung cards */}
        <div style={{ border: "1px solid var(--line)", borderRadius: "0 0 var(--r-sm) var(--r-sm)", overflow: "hidden" }}>
          {rungs.length === 0 && <div style={{ padding: "18px 12px", textAlign: "center", font: "var(--t-micro)", color: "var(--fg4)" }}>No rungs. Add a resolution below.</div>}
          {rungs.map((r) => {
            const key = rk(r.width, r.height);
            const open = openKeys.has(key);
            const codec = r.videoCodec ?? work.videoCodec;
            const lv = levelsApply(codec);
            const f = r.filter ?? 0;
            const profileOpts = profileOptionsFor(codec);
            const summaryBits: string[] = [
              `${CODECS.find((c) => c.id === codec)?.label ?? codec}`,
              lv ? `L${r.level && r.level !== "auto" ? r.level : "auto"}` : "L n/a",
              r.bitrateKbps && r.bitrateKbps > 0 ? `${r.bitrateKbps} kb/s` : "auto kb/s",
              `${r.frameRate && r.frameRate > 0 ? r.frameRate : work.frameRate || 30} fps`,
            ];
            if (f !== 0) summaryBits.push(filterLabel(f));
            if (!audioOff) summaryBits.push(`${(r.audioCodec || work.audioCodec || "aac").toUpperCase()} ${r.audioBitrateKbps || 128}k`);
            return (
              <div key={key} style={{ background: open ? INK_700 : "transparent", borderBottom: "1px solid var(--line)", boxShadow: open ? "inset 2px 0 0 var(--blue-500)" : "none" }}>
                <div onClick={() => openRung(key)} title="Click to edit this rung" style={{ display: "grid", gridTemplateColumns: GRID, gap: 10, alignItems: "center", padding: "7px 10px", cursor: "pointer" }}>
                  {/* enabled = ladder membership; the toggle removes/adds the rung. */}
                  <button className="ae-b" onClick={(e) => { e.stopPropagation(); removeRung(key); }} disabled={enabledCount <= 1} title={enabledCount <= 1 ? "At least one rung is required" : "Disable (remove) rung"} style={checkBox(true)}>
                    <Icon name="check" size={11} color={CHECK_MARK} />
                  </button>
                  <input value={r.name} onChange={(e) => setRung(key, { name: e.target.value })} style={{ width: "100%", minWidth: 0, minHeight: 26, padding: "0 7px", borderRadius: "var(--r-sm)", background: FIELD_BG, border: "1px solid var(--line)", color: "#fff", font: "500 12px var(--font-sans)", outline: "none" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <DimInput value={r.width} min={16} step={2} onCommit={(v) => setRung(key, { width: v })} />
                    <span style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>×</span>
                    <DimInput value={r.height} min={16} step={2} onCommit={(v) => setRung(key, { height: v })} />
                  </div>
                  <div style={{ font: "400 11px var(--font-mono)", color: "var(--fg3)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summaryBits.join(" · ")}</div>
                  <button
                    className="ae-b"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOpen(key);
                    }}
                    title="Edit rung"
                    style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "var(--r-xs)", border: "1px solid var(--line)", background: "transparent", color: "var(--fg2)", cursor: "pointer" }}
                  >
                    <Icon name="chevron-down" size={13} style={{ transform: open ? "rotate(180deg)" : "none" }} />
                  </button>
                  <button className="ae-b" onClick={(e) => { e.stopPropagation(); removeRung(key); }} disabled={enabledCount <= 1} title="Remove rung" style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "var(--r-xs)", border: "1px solid transparent", background: "transparent", color: enabledCount <= 1 ? "transparent" : "var(--fg4)", cursor: enabledCount <= 1 ? "default" : "pointer" }}>
                    <Icon name="x" size={12} />
                  </button>
                </div>

                {open && (
                  <div style={{ padding: "9px 10px 11px", borderTop: "1px solid var(--line)", display: "grid", gridTemplateColumns: "repeat(9, minmax(0,1fr))", gap: 7, alignItems: "start" }}>
                    <div>
                      <div style={microLabel}>Codec</div>
                      <select value={codec} onChange={(e) => setRungCodec(key, e.target.value as VideoCodec)} style={sel}>
                        {allowedCodecs.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={microLabel}>Profile</div>
                      <select value={r.profile ?? profileOpts[0].id} onChange={(e) => setRung(key, { profile: e.target.value })} style={sel}>
                        {profileOpts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={microLabel}>Level</div>
                      {lv ? (
                        <select value={r.level ?? "auto"} onChange={(e) => setRung(key, { level: e.target.value })} style={sel}>
                          {LEVELS.map((l) => (
                            <option key={l} value={l}>
                              {l === "auto" ? "Auto" : l}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div style={{ minHeight: 28, display: "flex", alignItems: "center", padding: "0 9px", borderRadius: "var(--r-sm)", background: INK_800, border: "1px solid var(--line)", font: "400 11px var(--font-mono)", color: "var(--fg4)" }}>n/a</div>
                      )}
                    </div>
                    <div>
                      <div style={microLabel}>{isCRF ? "Max" : "Target"} kb/s</div>
                      <NumInput value={r.bitrateKbps ?? 0} min={0} max={200000} step={50} placeholder="auto" onCommit={(v) => setRung(key, { bitrateKbps: v })} style={inp} />
                    </div>
                    <div>
                      <div style={microLabel}>Frame rate</div>
                      <select value={r.frameRate ?? 0} onChange={(e) => setRung(key, { frameRate: Number(e.target.value) })} style={sel}>
                        <option value={0}>Source</option>
                        {FPS_STEPS.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6, marginBottom: 5 }}>
                        <span style={{ ...microLabel, marginBottom: 0 }}>Filter</span>
                        <span style={{ font: "600 10px var(--font-mono)", color: f === 0 ? "var(--fg3)" : "var(--blue-400)", whiteSpace: "nowrap" }}>{filterLabel(f)}</span>
                      </div>
                      <DragSlider label="" value={f} min={-10} max={10} step={1} onChange={(v) => setRung(key, { filter: Math.round(v) })} />
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3, font: "400 8px var(--font-sans)", letterSpacing: ".04em", color: "var(--fg4)" }}>
                        <span>◀ SHARP</span>
                        <span>0</span>
                        <span>SMOOTH ▶</span>
                      </div>
                    </div>

                    {!audioOff && (
                      <>
                        <div>
                          <div style={microLabel}>Audio codec</div>
                          <select value={r.audioCodec ?? ""} onChange={(e) => setRung(key, { audioCodec: e.target.value as AudioCodec })} style={sel}>
                            <option value="">Container</option>
                            {allowedAudioCodecs.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <div style={microLabel}>Channels</div>
                          <select value={r.audioChannels ?? 0} onChange={(e) => setRung(key, { audioChannels: Number(e.target.value) })} style={sel}>
                            <option value={0}>Source</option>
                            <option value={2}>Stereo</option>
                            <option value={1}>Mono</option>
                          </select>
                        </div>
                        <div>
                          <div style={microLabel}>Audio kb/s</div>
                          <NumInput value={r.audioBitrateKbps ?? 0} min={0} max={512} step={16} placeholder="def" onCommit={(v) => setRung(key, { audioBitrateKbps: v })} style={inp} />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* add custom row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9, flexWrap: "wrap" }}>
          <span style={{ font: "var(--t-micro)", color: "var(--fg3)", letterSpacing: ".04em" }}>Add custom</span>
          <input value={cw} onChange={(e) => setCw(e.target.value)} placeholder="1920" inputMode="numeric" style={{ width: 76, minHeight: 28, padding: "0 8px", borderRadius: "var(--r-sm)", background: FIELD_BG, border: "1px solid var(--line)", color: "#fff", font: "400 12px var(--font-mono)", outline: "none", textAlign: "right" }} />
          <span style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>×</span>
          <input value={ch} onChange={(e) => setCh(e.target.value)} placeholder="1080" inputMode="numeric" onKeyDown={(e) => e.key === "Enter" && addCustom()} style={{ width: 76, minHeight: 28, padding: "0 8px", borderRadius: "var(--r-sm)", background: FIELD_BG, border: "1px solid var(--line)", color: "#fff", font: "400 12px var(--font-mono)", outline: "none", textAlign: "right" }} />
          <button className="ae-b" onClick={addCustom} disabled={enabledCount >= 12} title={enabledCount >= 12 ? "Ladder is full (12)" : "Add rung"} style={{ minHeight: 28, padding: "0 14px", borderRadius: "var(--r-xs)", font: "600 10px var(--font-sans)", letterSpacing: ".08em", textTransform: "uppercase", color: "var(--blue-300)", background: "var(--blue-tint)", border: "1px solid var(--blue-500)", cursor: enabledCount >= 12 ? "not-allowed" : "pointer", opacity: enabledCount >= 12 ? 0.6 : 1 }}>
            Add rung
          </button>
          {missingSteps.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: "auto", flexWrap: "wrap" }}>
              {missingSteps.map((s) => (
                <TChip key={s.name} onClick={() => quickAdd(s)}>
                  + {s.name}
                </TChip>
              ))}
            </div>
          )}
        </div>

        {/* The "Not yet supported" mockup block (two-pass, deinterlace, colour/
            transfer, sample rate, extended audio codecs) is intentionally not
            rendered: it exposed unshipped target-layout controls. */}
      </div>

      {/* FOOTER */}
      <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 12, padding: "10px 18px", borderTop: "1px solid var(--line)", background: INK_850 }}>
        <div style={{ flex: 1, minWidth: 0, font: "var(--t-micro)", color: err ? "var(--err)" : nameMissing ? "var(--warn)" : "var(--fg3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {err ?? (nameMissing ? "A profile name is required." : footerSummary)}
        </div>
        <button className="ae-b" onClick={onClose} style={{ height: 32, padding: "0 14px", borderRadius: "var(--r-sm)", border: "1px solid var(--line-strong)", background: "transparent", color: "var(--fg2)", font: "600 11px var(--font-sans)", letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" }}>
          Cancel
        </button>
        <button className="ae-b" onClick={save} disabled={!nameValid} title={nameValid ? "Save profile" : "Enter a profile name first"} style={{ height: 32, padding: "0 16px", borderRadius: "var(--r-sm)", font: "600 11px var(--font-sans)", letterSpacing: ".08em", textTransform: "uppercase", color: "#fff", background: nameValid ? "var(--blue-500)" : "var(--line-strong)", border: "1px solid transparent", cursor: nameValid ? "pointer" : "not-allowed", opacity: nameValid ? 1 : 0.7 }}>
          Save profile
        </button>
      </div>
    </div>
  );
}

// NumInput edits an integer/float field with a local draft so live PATCH does
// not fight typing; commits on blur/Enter. Blank commits 0 ("use default").
function NumInput({ value, min, max, step, placeholder, onCommit, style }: { value: number; min: number; max: number; step: number; placeholder?: string; onCommit: (v: number) => void; style: CSSProperties }) {
  const [v, setV] = useState(value > 0 ? String(value) : "");
  useEffect(() => setV(value > 0 ? String(value) : ""), [value]);
  const commit = () => {
    const n = parseFloat(v);
    if (!isFinite(n) || n <= 0) return onCommit(0);
    onCommit(Math.min(max, Math.max(min, step >= 1 ? Math.round(n) : n)));
  };
  return (
    <input
      value={v}
      placeholder={placeholder}
      inputMode="decimal"
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      style={{ ...style, textAlign: "right" }}
    />
  );
}

// RatioInput is a small float input (VBV ratios) that never commits 0 — blank
// reverts to the current value so a VBR ratio can only be a valid multiple.
function RatioInput({ value, min, max, title, onCommit, style }: { value: number; min: number; max: number; title: string; onCommit: (v: number) => void; style: CSSProperties }) {
  const [v, setV] = useState(String(value));
  useEffect(() => setV(String(value)), [value]);
  const commit = () => {
    const n = parseFloat(v);
    if (!isFinite(n)) return setV(String(value));
    onCommit(Math.min(max, Math.max(min, n)));
  };
  return <input value={v} title={title} inputMode="decimal" onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} style={{ ...style, textAlign: "right" }} />;
}

// DimInput is the compact width/height cell in a collapsed rung row.
function DimInput({ value, min, step, onCommit }: { value: number; min: number; step: number; onCommit: (v: number) => void }) {
  const [v, setV] = useState(String(value));
  useEffect(() => setV(String(value)), [value]);
  const commit = () => {
    let n = Math.round(parseFloat(v));
    if (!isFinite(n) || n < min) return setV(String(value));
    if (n % 2) n += 1;
    onCommit(n);
  };
  return <input value={v} inputMode="numeric" step={step} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} style={{ width: 56, minHeight: 26, padding: "0 6px", borderRadius: "var(--r-sm)", background: FIELD_BG, border: "1px solid var(--line)", color: "var(--fg2)", font: "400 11px var(--font-mono)", outline: "none", textAlign: "right" }} />;
}

// Scaling icons (inline SVG, matching the design's fit-pad / fill-crop / stretch
// glyphs). currentColor picks up the active/inactive button color.
function ScaleFitIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="1.5" />
      <rect x="7" y="7" width="10" height="10" rx="1" fill="currentColor" opacity="0.28" stroke="none" />
      <path d="M7 7v10M17 7v10" />
    </svg>
  );
}
function ScaleFillIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="1.5" />
      <path d="M2 9h20M2 15h20" opacity="0.5" />
      <path d="M8 4v16M16 4v16" strokeDasharray="2 2" opacity="0.55" />
    </svg>
  );
}
function ScaleStretchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="1.5" />
      <path d="M6 12h12M6 12l2.5-2.5M6 12l2.5 2.5M18 12l-2.5-2.5M18 12l-2.5 2.5" />
    </svg>
  );
}
